import { useEffect, useState } from "react";

// The gpuvm image servers listen only on gpuvm's localhost, so prompts never
// cross the network unencrypted. They are reached through SSH forwards:
//   deployed site  same-origin nginx proxy on passer.scpdx.net (HTTPS), which
//                  reaches gpuvm over the gpuvm-flux-tunnel SSH forward
//   local dev      the Mac's gpuvm SSH tunnel (net.gpuvm.museglimmer.tunnel)
//                  on 127.0.0.1:<port>
const GPUVM_SERVERS = {
  "http://195.230.127.226:8510": "/flux/gpu",    // FLUX.2-dev  (flux-api)
  "http://195.230.127.226:8511": "/flux1/gpu",   // FLUX.1-dev  (flux1-api)
  "http://195.230.127.226:8512": "/klein/gpu",   // FLUX.2 [klein] 9B (flux2-klein-api)
  "http://195.230.127.226:8513": "/qwen/gpu",    // Qwen-Image-Edit-2511 (qwen-image-api)
  "http://195.230.127.226:8514": "/moss/gpu",    // MOSS-Image, text-to-image only (moss-image-api)
  "http://195.230.127.226:8516": "/qwen21/gpu",  // Qwen-Image-2.1 Turbo, generate + edit (qwen21-image-api)
  "http://195.230.127.226:8517": "/ocr/gpu",     // OCR page: PaddleOCR-VL-1.6 + GLM-OCR (ocr-api)
};

// Step-distilled servers and their settings: FLUX.2 [klein] and Qwen-Image-Edit
// with Lightning (4 steps), MOSS-Image with its DMD LoRA (8 steps); no guidance.
// The pages switch to these when such a port is picked and back to their own
// defaults when it is left. minSize: Qwen and MOSS are trained at ~1024-1328 px
// and come out mushy and painted at 512, so Text to Image scales the size up.
// fixedGuidance: on Qwen and MOSS guidance > 1 runs real CFG on top of the
// distillation and turns images grainy, so the pages lock it at 1.0. klein
// ignores guidance (is_distilled), so its field stays free and harmless.
export const PORT_PRESETS = {
  8512: { steps: 4, guidance: 1.0 },
  8513: { steps: 4, guidance: 1.0, minSize: 1024, fixedGuidance: true },
  8514: { steps: 8, guidance: 1.0, minSize: 1024, fixedGuidance: true },
  8516: { steps: 8, guidance: 1.0, minSize: 1024, fixedGuidance: true },  // Qwen-Image-2.1 Turbo
};
export const PAGE_PRESET = { steps: 20, guidance: 7.5 };

export const imageApiBase = (apiUrl, port) => {
  const direct = `${apiUrl}:${port}`;
  const proxyPath = GPUVM_SERVERS[direct];
  if (!proxyPath) return direct;
  return window.location.protocol === "https:"
    ? `${window.location.origin}${proxyPath}`
    : `http://127.0.0.1:${port}`;
};

// Asked by the pages' stall watchdogs before they give up: true while the server
// still reports a job (or a model load) in progress. Safari holds back a
// background tab's timers and socket messages, and when the tab comes back it
// can run the watchdog first, so a job that was fine looked like 2 minutes
// without progress. Waiting for this answer also lets the held-back messages
// (progress, "complete") reach the page first.
export const serverStillWorking = async (base) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const status = await (await fetch(`${base}/`, { signal: controller.signal })).json();
    return !!(status.generation_in_progress || status.model_loading);
  } catch (e) {
    return false;   // not answering: treat as lost
  } finally {
    clearTimeout(timer);
  }
};

// The LoRA file the selected image server is configured with (from its
// status route), or null. Lets the pages show a strength slider only when
// there is a LoRA to weigh.
export const useServerLora = (apiUrl, port) => {
  const [loraFile, setLoraFile] = useState(null);
  // Re-check when the tab regains focus: the LoRA may have been switched on
  // the Admin page in another tab meanwhile.
  const [focusCount, setFocusCount] = useState(0);
  useEffect(() => {
    const onFocus = () => setFocusCount((n) => n + 1);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);
  // A different server: forget the previous one's LoRA until this one answers
  useEffect(() => setLoraFile(null), [apiUrl, port]);
  useEffect(() => {
    const base = imageApiBase(apiUrl, port);
    // From the https site an http server (e.g. Local 127.0.0.1) is blocked as
    // mixed content anyway; skip it rather than log errors.
    if (window.location.protocol === "https:" && base.startsWith("http:")) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    fetch(`${base}/`, { signal: controller.signal })
      .then((r) => r.json())
      .then((j) => setLoraFile(j.lora_file || null))
      .catch(() => {})   // server down or busy loading: just no slider
      .finally(() => clearTimeout(timer));
    return () => { controller.abort(); clearTimeout(timer); };
  }, [apiUrl, port, focusCount]);
  return loraFile;
};
